import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Logo } from "@/components/layout/Logo";
import Seo from "@/components/common/Seo";
import { useAuth } from "@/context/AuthContext";

export default function Login({ initialMode = "login" }) {
  const [params] = useSearchParams();
  const ref = params.get("ref") || undefined;
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ name: "", email: "", password: "", dob: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const res = mode === "login" ? await login(form.email, form.password) : await register(form.name, form.email, form.password, ref, form.dob || undefined);
    setBusy(false);
    if (res.ok) {
      toast.success(mode === "login" ? "Welcome back." : "Account created. Welcome to Reverence.");
      navigate(res.role === "admin" ? "/admin" : "/dashboard");
    } else setError(res.error);
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const isSignup = mode === "signup";

  return (
    <div data-testid="login-page" className="grid min-h-screen lg:grid-cols-2">
      <Seo title={isSignup ? "Create Account | Reverence Autos" : "Login | Reverence Autos"}
        description="Sign in to manage your Reverence Autos bookings, subscription and referral rewards." path={isSignup ? "/signup" : "/login"} />
      <div className="relative hidden overflow-hidden bg-brand-charcoal lg:block">
        <img src="/gallery/img_7040.webp" alt="Reverence detailer polishing a car" className="absolute inset-0 h-full w-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-charcoal via-brand-charcoal/50 to-transparent" />
        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-white">
          <Logo dark />
          <div>
            <h2 className="font-serif text-4xl font-bold leading-tight">Fall in love with<br />your car again.</h2>
            <p className="mt-4 max-w-sm text-brand-silver">Track bookings, manage your subscription, and earn loyalty & referral rewards.</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col justify-center bg-white px-6 py-12 sm:px-12 lg:px-20">
        <Link to="/" className="mb-10 inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-red"><ArrowLeft className="h-4 w-4" /> Back home</Link>
        <div className="mx-auto w-full max-w-sm">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h1 className="font-serif text-4xl font-bold text-brand-charcoal">{isSignup ? "Create account" : "Welcome back"}</h1>
          <p className="mt-2 text-brand-muted">{isSignup ? "Join Reverence in seconds" : "Sign in to continue"}</p>

          <button onClick={googleLogin} data-testid="google-login-button"
            className="mt-8 flex w-full items-center justify-center gap-3 rounded-full border border-brand-silver/50 bg-white py-3.5 text-sm font-medium text-brand-charcoal transition-colors hover:bg-brand-offwhite">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" /> Continue with Google
          </button>
          <div className="my-6 flex items-center gap-4"><div className="h-px flex-1 bg-brand-silver/40" /><span className="text-xs uppercase tracking-wider text-brand-muted">or</span><div className="h-px flex-1 bg-brand-silver/40" /></div>

          <form onSubmit={submit} className="space-y-4">
            {isSignup && <input data-testid="auth-name-input" required value={form.name} onChange={upd("name")} placeholder="Full name" className="w-full rounded-xl border border-brand-silver/50 px-4 py-3.5 text-sm focus:border-brand-red focus:outline-none focus:ring-1 focus:ring-brand-red" />}
            <input data-testid="auth-email-input" type="email" required value={form.email} onChange={upd("email")} placeholder="Email" className="w-full rounded-xl border border-brand-silver/50 px-4 py-3.5 text-sm focus:border-brand-red focus:outline-none focus:ring-1 focus:ring-brand-red" />
            <input data-testid="auth-password-input" type="password" required value={form.password} onChange={upd("password")} placeholder="Password" className="w-full rounded-xl border border-brand-silver/50 px-4 py-3.5 text-sm focus:border-brand-red focus:outline-none focus:ring-1 focus:ring-brand-red" />
            {isSignup && (
              <div>
                <label className="mb-1.5 block text-xs font-medium text-brand-muted">Date of birth <span className="text-brand-muted/60">(optional — unlock a birthday-month wash when you join Members)</span></label>
                <input data-testid="auth-dob-input" type="date" value={form.dob} onChange={upd("dob")} max={new Date().toISOString().split("T")[0]} className="w-full rounded-xl border border-brand-silver/50 px-4 py-3.5 text-sm text-brand-charcoal focus:border-brand-red focus:outline-none focus:ring-1 focus:ring-brand-red" />
              </div>
            )}
            {error && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="auth-error" className="text-sm text-brand-red">{error}</motion.p>}
            <button data-testid="auth-submit-button" disabled={busy} className="btn-red w-full">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (isSignup ? "Create account" : "Sign in")}</button>
          </form>

          <p className="mt-6 text-center text-sm text-brand-muted">
            {isSignup ? "Already have an account?" : "New to Reverence?"}{" "}
            <button data-testid="auth-toggle-mode" onClick={() => { setMode(isSignup ? "login" : "signup"); setError(""); }} className="font-semibold text-brand-red hover:underline underline-offset-4">
              {isSignup ? "Sign in" : "Create one"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
